import {
  IPayerOverrideRow,
  IPayerOverrideSaveRequest,
} from "types/BenefitSelection/BenefitSelection";

import { api } from ".";

/**
 * CLE-2792 — `BenefitPayerProfile`: what a payer's plans really charge, where the response is wrong
 * or silent.
 *
 * SuperAdmin on the reads as well as the writes, because an override is a statement about a payer
 * that applies to every account that payer serves — adding one hands a dollar figure to estimates
 * that carry none today.
 *
 * 🚨 THE WIRE UNIT IS `kind`, NEVER `mode`. The API derives `mode` and `afterSource` from it; no
 * response carries `mode` and no request may send one. The three kinds are the three sentences the
 * screen shows — see `payerOverrideWording.ts`.
 */
const benefitPayerProfileQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Every override, newest first. With `accountId`, the ones that could govern that account —
     * which INCLUDES the rows naming no account, since those govern it too.
     */
    getPayerOverrides: builder.query<
      { data: IPayerOverrideRow[]; message: string },
      { accountId?: string } | void
    >({
      query: (args) =>
        args?.accountId
          ? `benefitPayerProfile?accountId=${encodeURIComponent(
              args.accountId
            )}`
          : "benefitPayerProfile",
      providesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
    /**
     * 🚨 A BODY WITH NO SCOPE FIELD NEEDS `appliesToEveryPayer: true`. Without it the API 400s,
     * which is the point: an override naming nothing applies to every payer and every account, and
     * the payload cannot tell that apart from a form nobody filled in.
     */
    createPayerOverride: builder.mutation<
      { data: IPayerOverrideRow; message: string },
      IPayerOverrideSaveRequest
    >({
      query: (body) => ({
        url: "benefitPayerProfile",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
    /** The whole row is written, so the body must carry every field, scope included. */
    updatePayerOverride: builder.mutation<
      { data: IPayerOverrideRow; message: string },
      { id: string; body: IPayerOverrideSaveRequest }
    >({
      query: ({ id, body }) => ({
        url: `benefitPayerProfile/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
    /** 🚨 A HARD DELETE — the table has no versioning. The API writes the row to `EventLog` first. */
    deletePayerOverride: builder.mutation<
      { data: { id: string }; message: string },
      string
    >({
      query: (id: string) => ({
        url: `benefitPayerProfile/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetPayerOverridesQuery,
  useCreatePayerOverrideMutation,
  useUpdatePayerOverrideMutation,
  useDeletePayerOverrideMutation,
} = benefitPayerProfileQueries;
